/**
 * CSV Upload Button Component
 *
 * A reusable component for uploading CSV files to bulk create device associations.
 * Displays upload progress, validation errors, and success summary.
 */

import { useState, useRef } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import { downloadDeviceAssociationsTemplate } from '@/utils/csvTemplate';

interface CSVUploadResponse {
  success: boolean;
  total_rows: number;
  created_devices: number;
  created_associations: number;
  errors: string[];
  warnings: string[];
}

interface CSVUploadButtonProps {
  clinicId: number;
  onUploadComplete?: () => void;
  variant?: string;
  size?: 'sm' | 'lg';
  className?: string;
}

export default function CSVUploadButton({
  clinicId,
  onUploadComplete,
  variant = 'outline-primary',
  size,
  className,
}: CSVUploadButtonProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<CSVUploadResponse | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      alert(t('admin.csvUpload.invalidFileType', 'File must be a CSV file (.csv extension)'));
      return;
    }

    setUploading(true);

    try {
      const response = await apiClient.uploadDeviceAssociationsCSV(clinicId, file);

      if (response.data && !response.error) {
        const uploadResult = response.data as CSVUploadResponse;
        setResult(uploadResult);
        setShowResult(true);

        if (uploadResult.success && onUploadComplete) {
          onUploadComplete();
        }
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      setResult({
        success: false,
        total_rows: 0,
        created_devices: 0,
        created_associations: 0,
        errors: [
          error instanceof Error
            ? error.message
            : t('admin.csvUpload.unexpectedError', 'An unexpected error occurred'),
        ],
        warnings: [],
      });
      setShowResult(true);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseResult = () => {
    setShowResult(false);
    setResult(null);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleButtonClick}
        disabled={uploading}
      >
        {uploading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {t('admin.csvUpload.uploading', 'Uploading...')}
          </>
        ) : (
          <>
            <i className="fas fa-file-csv me-2" />
            {t('admin.csvUpload.uploadCSV', 'Upload CSV')}
          </>
        )}
      </Button>

      {/* Result Modal */}
      <Modal show={showResult} onHide={handleCloseResult} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {result?.success ? (
              <>
                <i className="fas fa-check-circle text-success me-2" />
                {t('admin.csvUpload.uploadSuccessful', 'Upload Successful')}
              </>
            ) : (
              <>
                <i className="fas fa-exclamation-triangle text-danger me-2" />
                {t('admin.csvUpload.uploadFailed', 'Upload Failed')}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {result && (
            <>
              {/* Summary */}
              <div className="mb-3">
                <h6>{t('admin.csvUpload.summary', 'Summary')}</h6>
                <ul className="list-unstyled mb-0">
                  <li>
                    <strong>{t('admin.csvUpload.totalRows', 'Total rows processed')}:</strong>{' '}
                    {result.total_rows}
                  </li>
                  <li>
                    <strong>{t('admin.csvUpload.devicesCreated', 'New devices created')}:</strong>{' '}
                    {result.created_devices}
                  </li>
                  <li>
                    <strong>
                      {t('admin.csvUpload.associationsCreated', 'Associations created')}:
                    </strong>{' '}
                    {result.created_associations}
                  </li>
                </ul>
              </div>

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <Alert variant="warning" className="mb-3">
                  <Alert.Heading className="h6">
                    <i className="fas fa-exclamation-triangle me-2" />
                    {t('admin.csvUpload.warnings', 'Warnings')} ({result.warnings.length})
                  </Alert.Heading>
                  <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}><small>{warning}</small></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <Alert variant="danger" className="mb-0">
                  <Alert.Heading className="h6">
                    <i className="fas fa-times-circle me-2" />
                    {t('admin.csvUpload.errors', 'Errors')} ({result.errors.length})
                  </Alert.Heading>
                  <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {result.errors.map((error, idx) => (
                      <li key={idx}><small>{error}</small></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Format Help */}
              {!result.success && (
                <div className="mt-3">
                  <small className="text-muted">
                    <strong>{t('admin.csvUpload.expectedFormat', 'Expected CSV format')}:</strong>
                    <pre className="bg-light p-2 mt-2" style={{ fontSize: '0.85em' }}>
                      device_id,start_time,end_time{'\n'}
                      DEV-001,2024-01-01T00:00:00Z,2024-12-31T23:59:59Z{'\n'}
                      DEV-002,2024-01-15,{'\n'}
                      DEV-003,,
                    </pre>
                  </small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={downloadDeviceAssociationsTemplate}
          >
            <i className="fas fa-download me-2" />
            {t('admin.csvUpload.downloadTemplate', 'Download Template')}
          </Button>
          <Button variant="secondary" onClick={handleCloseResult}>
            {t('common.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
